/**
 * Bosnian strings for the tasks screens.
 *
 * The base every other language is measured against. Enum labels are NOT
 * here and must never be — statuses, types, priorities, roles and months
 * come from /api/meta, and a second copy is the drift that put IN_PROGRESS
 * on a manager's badge in the legacy app.
 */

export const tasks = {
  // ---- The list ----------------------------------------------------
  /*
   * The two presses the whole board depends on, said at the top of the screen.
   *
   * `{start}` and `{done}` are the buttons' own labels, passed in at render
   * time rather than written out a second time here: they are two keys down in
   * this same file, and a sentence quoting a button that has since been
   * reworded is worse than one that does not name it at all.
   */
  'tasks.help':
    'Kad počnete raditi na zadatku pritisnite „{start}”, a kad ga završite „{done}” — tako nadležni zna dokle se stiglo. Zadatak kojem je prošao rok stoji crven na početnom ekranu.',

  'tasks.title': 'Moji zadaci',
  'tasks.subtitle.openOnly': 'Nezavršeni zadaci',
  'tasks.subtitle.all': 'Svi zadaci',
  'tasks.filter.showAll': 'Prikaži sve',
  'tasks.filter.openOnly': 'Samo nezavršene',

  /*
   * Two whole sentences rather than one with a plural helper. The banner is
   * read at the start of a shift and "1 zadataka" in it is the kind of thing
   * that makes people stop trusting the number beside it.
   */
  'tasks.overdue.one': 'Jedan zadatak je van roka.',
  'tasks.overdue.many': '{count} zadataka je van roka.',

  'tasks.empty.openTitle': 'Nemate otvorenih zadataka',
  'tasks.empty.allTitle': 'Nemate zadataka',
  'tasks.empty.manager':
    'Ovdje su zadaci koji su dodijeljeni vama. Zadatak uvijek pripada nekoj prijavi — dodijelite ga dugmetom „Novi zadatak” ili sa same prijave.',
  'tasks.empty.worker': 'Zadatke dodjeljuje nadležni sa konkretne prijave.',

  // ---- One task ----------------------------------------------------
  'tasks.card.due': 'Rok: {date}',
  'tasks.card.noDue': 'Bez roka',
  'tasks.card.openSubmission': 'otvori prijavu',
  // Not a status: `isOverdue` is a flag the client renders itself, so unlike
  // `statusLabel` beside it this one is ours to translate.
  'tasks.card.overdue': 'Van roka',
  // The two transitions a worker may make on their own task. Verbs, not the
  // status names they lead to — those come back on `statusLabel`.
  'tasks.action.start': 'Započni',
  'tasks.action.markDone': 'Označi kao završen',
  'tasks.updated': 'Zadatak je ažuriran.',
  'tasks.updateFailed': 'Ažuriranje nije uspjelo.',

  // ---- Assigning work ----------------------------------------------
  'tasks.new': 'Novi zadatak',
  'tasks.new.cancel': 'Odustani',
  'tasks.new.submit': 'Dodijeli zadatak',
  'tasks.new.submission': 'Prijava',
  'tasks.new.submission.hint':
    'Zadatak se uvijek veže za prijavu. Prikazane su otvorene prijave.',
  'tasks.new.submission.placeholder': 'Izaberite prijavu',
  'tasks.new.submission.none':
    'Nema otvorenih prijava. Zadatak se dodjeljuje na prijavu, pa je prvo treba evidentirati.',
  'tasks.new.title': 'Zadatak',
  'tasks.new.title.placeholder': 'npr. Zamijeniti ležaj na hoblu',
  'tasks.new.assignee': 'Zadužen',
  'tasks.new.assignee.placeholder': 'Izaberite osobu',
  'tasks.new.description': 'Opis',
  'tasks.new.description.placeholder':
    'Neobavezno — šta tačno treba uraditi, koji dijelovi su potrebni.',
  'tasks.new.dueDate': 'Rok',
  'tasks.new.dueDate.hint': 'Ne može biti u prošlosti.',
  'tasks.new.assigned': 'Zadatak je dodijeljen.',
  'tasks.new.failed': 'Dodjela nije uspjela.',

  // ---- Getting to the work: the machine sticker --------------------
  'tasks.scan.title': 'Skeniraj QR kod',
  'tasks.scan.subtitle':
    'Usmjerite kameru na naljepnicu na mašini. Prijava se otvara sa već izabranim radnim centrom.',
  'tasks.scan.searching': 'Tražim QR kod…',
  'tasks.scan.errorTitle': 'Skeniranje nije moguće',
  'tasks.scan.unknownCode': 'QR kod nije prepoznat. Obratite se nadležnom za novu naljepnicu.',
  'tasks.scan.cameraBlocked':
    'Pristup kameri nije moguć. Dozvolite kameru u podešavanjima pregledača ili izaberite radni centar ručno.',
  'tasks.scan.pickManually': 'Izaberi radni centar ručno',

  // The same sticker opened by the phone's own camera app, which lands on
  // /qr/{token} rather than inside the scanner.
  'tasks.qr.checkingSession': 'Provjera sesije…',
  'tasks.qr.opening': 'Otvaram radni centar…',
  'tasks.qr.unknownTitle': 'Nepoznat QR kod',
  'tasks.qr.unknownCode':
    'QR kod nije prepoznat. Naljepnica je možda zamijenjena — javite nadležnom.',

  // The shared camera view. Worded without saying what is being scanned,
  // because the same component reads a machine sticker and a worker card.
  'tasks.scanner.starting': 'Pokretanje kamere…',
  'tasks.scanner.cameraBlocked':
    'Pristup kameri nije moguć. Dozvolite kameru u podešavanjima pregledača.',
} as const

export type TasksSlice = Record<keyof typeof tasks, string>
